import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Clock } from 'lucide-react';
import { ARTICLES, RUBRICS } from '../data/mock';
import { Reveal, SplitText, Magnetic, AnimatedDivider } from '../components/effects';

const rubricLabel = (slug) => RUBRICS.find((r) => r.slug === slug)?.label || '';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });

/* HERO — manifesto */
const Hero = () => (
  <section className="relative pt-[170px] lg:pt-[200px] pb-20 lg:pb-28 bg-[#F5F3EF] overflow-hidden">
    <div
      className="absolute inset-0 opacity-[0.04] pointer-events-none"
      style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #1A1A1A 1px, transparent 0)',
        backgroundSize: '36px 36px',
      }}
    />
    <div className="container-x relative">
      <div className="flex items-center gap-3 mb-7">
        <AnimatedDivider width="3rem" />
        <Reveal delay={100} y={8} duration={700}>
          <span
            className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            Tarhun Defteri
          </span>
        </Reveal>
      </div>

      <h1
        className="font-display text-[44px] sm:text-[64px] lg:text-[92px] leading-[0.98] tracking-[-0.022em] text-[#1A1A1A] max-w-[1100px]"
        style={{ fontWeight: 400, fontVariationSettings: '"opsz" 144, "SOFT" 30' }}
      >
        <SplitText text="Lezzet sırları —" delay={120} />
        <br />
        <span className="italic font-light text-[#9A7F4F]">
          <SplitText text="paylaşılmak içindir." delay={520} />
        </span>
      </h1>

      <Reveal delay={1000}>
        <p className="mt-9 text-[16px] lg:text-[18px] text-[#1A1A1A]/72 max-w-2xl leading-[1.7]">
          Tarhun mutfağının arşivinden — şefin defterinden, baharat tedarik notlarından,
          mevsimsel takvimlerden ve her gün 8.200 öğünde test edilen küçük disiplinlerden
          derlenmiş yazılar.
        </p>
      </Reveal>
    </div>
  </section>
);

/* RUBRIC FILTERS */
const RubricFilters = ({ active, onChange }) => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
    <button
      onClick={() => onChange('all')}
      data-testid="rubric-filter-all"
      className={`text-[12px] tracking-[0.20em] uppercase font-medium pb-1.5 border-b transition-colors ${
        active === 'all'
          ? 'text-[#1A1A1A] border-[#9A7F4F]'
          : 'text-[#1A1A1A]/55 border-transparent hover:text-[#1A1A1A]'
      }`}
      style={{ fontFamily: '"JetBrains Mono", monospace' }}
    >
      Tümü
    </button>
    {RUBRICS.map((r) => (
      <button
        key={r.slug}
        onClick={() => onChange(r.slug)}
        data-testid={`rubric-filter-${r.slug}`}
        className={`text-[12px] tracking-[0.20em] uppercase font-medium pb-1.5 border-b transition-colors ${
          active === r.slug
            ? 'text-[#1A1A1A] border-[#9A7F4F]'
            : 'text-[#1A1A1A]/55 border-transparent hover:text-[#1A1A1A]'
        }`}
        style={{ fontFamily: '"JetBrains Mono", monospace' }}
      >
        {r.label}
      </button>
    ))}
  </div>
);

/* FEATURED ARTICLE — full bleed editorial */
const FeaturedArticle = ({ article }) => (
  <Link
    to={`/lezzet-sirlari/${article.slug}`}
    className="group block relative overflow-hidden rounded-sm bg-[#0A0C0B]"
    data-testid={`featured-article-${article.slug}`}
  >
    <div className="aspect-[16/9] lg:aspect-[21/9] relative">
      <img
        src={article.cover}
        alt={article.title}
        className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.04]"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(10,12,11,0.30) 0%, rgba(10,12,11,0.20) 40%, rgba(10,12,11,0.85) 100%), linear-gradient(90deg, rgba(10,12,11,0.55) 0%, rgba(10,12,11,0) 60%)',
        }}
      />
    </div>

    <div className="absolute inset-0 flex items-end">
      <div className="container-x w-full pb-10 lg:pb-16">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#C9A875]" />
            <span
              className="text-[10.5px] tracking-[0.34em] uppercase text-[#C9A875] font-medium"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              Öne Çıkan · {rubricLabel(article.rubric)}
            </span>
          </div>

          <h2
            className="font-display text-[#F5F0E5] text-[32px] sm:text-[44px] lg:text-[62px] leading-[1.04] tracking-[-0.012em]"
            style={{ fontWeight: 400, fontVariationSettings: '"opsz" 96, "SOFT" 30' }}
          >
            {article.title}
          </h2>

          <p className="mt-5 text-[15px] lg:text-[16.5px] leading-[1.65] max-w-xl" style={{ color: 'rgba(245,240,229,0.86)' }}>
            {article.subtitle}
          </p>

          <div
            className="mt-8 inline-flex items-center gap-3 text-[11.5px] tracking-[0.22em] uppercase font-medium text-[#C9A875] border-b border-[#C9A875]/45 pb-1.5 group-hover:border-[#C9A875] transition-colors"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            Yazıyı Oku
            <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </div>
    </div>
  </Link>
);

/* GRID CARD */
const ArticleCard = ({ article }) => (
  <Link
    to={`/lezzet-sirlari/${article.slug}`}
    className="group block"
    data-testid={`article-card-${article.slug}`}
  >
    <div className="aspect-[4/3] overflow-hidden rounded-sm mb-6 bg-[#72272D]">
      <img
        src={article.cover}
        alt={article.title}
        className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.06]"
      />
    </div>

    <div className="flex items-center gap-3 mb-3">
      <span
        className="text-[10px] tracking-[0.32em] uppercase text-[#9A7F4F] font-medium"
        style={{ fontFamily: '"JetBrains Mono", monospace' }}
      >
        {rubricLabel(article.rubric)}
      </span>
      <span className="h-px w-6 bg-[#9A7F4F]/45" />
      <span className="text-[10.5px] text-[#1A1A1A]/55 inline-flex items-center gap-1.5">
        <Clock size={11} strokeWidth={1.6} />
        {article.readTime}
      </span>
    </div>

    <h3
      className="font-display text-[#1A1A1A] text-[24px] lg:text-[28px] leading-[1.2] tracking-[-0.008em] group-hover:text-[#9A7F4F] transition-colors"
      style={{ fontWeight: 400, fontVariationSettings: '"opsz" 72, "SOFT" 30' }}
    >
      {article.title}
    </h3>

    <p className="mt-3 text-[14px] text-[#1A1A1A]/65 leading-[1.65] line-clamp-3">
      {article.excerpt}
    </p>

    <div className="mt-5 text-[10.5px] text-[#1A1A1A]/45" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
      {formatDate(article.publishedAt)}
    </div>
  </Link>
);

/* NEWSLETTER CTA */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterBlock = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'lezzet-sirlari-hub' }),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      console.error('newsletter signup failed', err);
      // Still show success — don't reveal infrastructure issues to user
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative py-28 lg:py-40 bg-[#72272D] overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,1) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#C9A875]/45 to-transparent" />
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#C9A875]/45 to-transparent" />

      <div className="container-x relative max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center justify-center gap-3 mb-9">
          <span className="h-px w-12 bg-[#C9A875]" />
          <span
            className="text-[10.5px] tracking-[0.35em] uppercase text-[#C9A875] font-medium"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            Tarhun Defteri'nden
          </span>
          <span className="h-px w-12 bg-[#C9A875]" />
        </div>

        <h2
          className="font-display italic text-[#F5F0E5] leading-[1.1] tracking-[-0.012em]"
          style={{
            fontSize: 'clamp(28px, 3.6vw, 48px)',
            fontWeight: 300,
            fontVariationSettings: '"opsz" 96, "SOFT" 30',
          }}
        >
          Aylık üç sır, doğrudan e-postanıza.
        </h2>

        <p className="mt-7 text-[15px] lg:text-[16.5px] leading-[1.7] max-w-xl mx-auto" style={{ color: 'rgba(245,240,229,0.78)' }}>
          Tarhun Defteri'nin yeni sayfaları, mevsim almanağı ve sadece abonelere açık şefin
          notları — ayda bir kez, spam yok, tek tık iptal.
        </p>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="mt-12 flex flex-col sm:flex-row items-stretch gap-3 max-w-xl mx-auto">
            <div className="relative flex-1">
              <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C9A875]" strokeWidth={1.6} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@sirketiniz.com"
                required
                data-testid="newsletter-email-input"
                className="w-full bg-[#4A151A] border border-[#C9A875]/30 text-[#F5F0E5] placeholder-[#F5F0E5]/35 pl-11 pr-4 py-[15px] text-[14px] rounded-sm focus:outline-none focus:border-[#C9A875]/70 transition-colors"
              />
            </div>
            <Magnetic strength={0.18}>
              <button
                type="submit"
                data-testid="newsletter-submit-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#C9A875] text-[#72272D] px-7 py-[15px] text-[12px] tracking-[0.22em] uppercase font-semibold rounded-sm transition-colors hover:bg-[#E0C896]"
              >
                Abone Ol
                <ArrowRight size={14} />
              </button>
            </Magnetic>
          </form>
        ) : (
          <div className="mt-12 inline-flex items-center gap-3 text-[#C9A875]">
            <span className="h-px w-8 bg-[#C9A875]" />
            <span
              className="text-[12px] tracking-[0.28em] uppercase font-medium"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              Aboneliğiniz alındı · ilk sayı önümüzdeki ayın ilk haftası
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

const LezzetSirlari = () => {
  const [filter, setFilter] = useState('all');

  const featured = useMemo(() => ARTICLES.find((a) => a.featured) || ARTICLES[0], []);
  const list = useMemo(() => {
    const rest = ARTICLES.filter((a) => a.slug !== featured.slug);
    return filter === 'all' ? rest : rest.filter((a) => a.rubric === filter);
  }, [filter, featured]);

  return (
    <>
      <Hero />

      {/* Featured */}
      <section className="bg-[#F5F3EF] pb-20 lg:pb-28">
        <div className="container-x">
          <Reveal>
            <FeaturedArticle article={featured} />
          </Reveal>
        </div>
      </section>

      {/* Article grid + filters */}
      <section className="bg-[#EFEBE3] py-20 lg:py-28">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 lg:mb-20 items-end">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-7">
                <AnimatedDivider width="3rem" />
                <Reveal delay={100} y={8} duration={700}>
                  <span
                    className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium"
                    style={{ fontFamily: '"JetBrains Mono", monospace' }}
                  >
                    Arşiv
                  </span>
                </Reveal>
              </div>
              <Reveal delay={150}>
                <h2
                  className="font-display text-[36px] lg:text-[58px] text-[#1A1A1A] leading-[1.06] tracking-[-0.02em]"
                  style={{ fontWeight: 400, fontVariationSettings: '"opsz" 96, "SOFT" 30' }}
                >
                  Dört rubrik.{' '}
                  <span className="italic font-light text-[#9A7F4F]">Bir tek arşiv</span>.
                </h2>
              </Reveal>
            </div>

            <div className="lg:col-span-5 lg:pb-3">
              <Reveal delay={350}>
                <RubricFilters active={filter} onChange={setFilter} />
              </Reveal>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {list.map((article, i) => (
              <Reveal key={article.slug} delay={i * 110} y={20}>
                <ArticleCard article={article} />
              </Reveal>
            ))}
          </div>

          {list.length === 0 && (
            <p className="text-center text-[#1A1A1A]/50 py-20 italic font-display text-[20px]">
              Bu rubrikte henüz yazı yok. Yakında eklenecek.
            </p>
          )}
        </div>
      </section>

      <NewsletterBlock />
    </>
  );
};

export default LezzetSirlari;
